import React from 'react';
import { extractCompanyBrand, extractCompanySuffix } from '../utils/company/company-name';
import { fixMojibake } from '../utils/text/fix-mojibake';

const COPY_SPACE_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'pre',
};

interface CompanyNameProps {
  /** The full company name including suffix */
  name: string;
  /** Optional className for the container */
  className?: string;
  /** Optional className for the main name part */
  mainNameClassName?: string;
  /** Optional className for the suffix part */
  suffixClassName?: string;
  /**
   * Draw the suffix as plain text in the surrounding colour (no chip), for surfaces whose colour
   * is not a role, such as an employer brand strip.
   */
  plainSuffix?: boolean;
  /** Whether to apply mojibake fixing */
  fixMojibake?: boolean;
  /**
   * Also move trailing non-brand words (Group, Finland, a region like Pohjanmaa) into the muted
   * suffix, so compact cards lead with the brand. Display only.
   */
  splitBrand?: boolean;
}

/**
 * Component to display a company name with the suffix shown in smaller, lightly highlighted text
 */
export function CompanyName({
  name,
  className = '',
  mainNameClassName = '',
  suffixClassName = '',
  plainSuffix = false,
  fixMojibake: shouldFixMojibake = true,
  splitBrand = false,
}: CompanyNameProps) {
  if (!name) return null;

  const processedName = shouldFixMojibake ? fixMojibake(name) : name;
  const { mainName, suffix } = splitBrand ? extractCompanyBrand(processedName) : extractCompanySuffix(processedName);

  if (!suffix) {
    return <span className={className}>{mainName}</span>;
  }

  return (
    <span className={className}>
      <span className={mainNameClassName}>{mainName}</span>
      {/* A real space so copied text reads "Acme Oy", not "AcmeOy": the visible gap is only a
          margin. Kept as a 1px clipped, white-space: pre span so it survives flex layouts (where a
          plain whitespace text node between items is dropped) without adding visual width. */}
      <span className="company-name-copy-space" style={COPY_SPACE_STYLE}>
        {' '}
      </span>
      <span
        className={`text-[0.7em] leading-none ml-0.5 ${
          plainSuffix ? 'opacity-75' : 'rounded-jc-info bg-jc-elevated px-1 py-0.5 text-jc-ink-secondary'
        } ${suffixClassName}`}
      >
        {suffix}
      </span>
    </span>
  );
}
